
import { useState, useEffect, useRef } from 'react';

const services = [
  {
    title: 'Kitchen Deep Clean',
    description:
      'Watch our team transform greasy kitchens into sparkling spaces. We clean every surface, appliance, and cabinet inside out.',
    icon: 'ri-restaurant-line',
    image:
      'https://public.readdy.ai/ai/img_res/cff9cef3d4ca9a70c88ccf702ad10d63.jpg',
    duration: '2:45',
  },
  {
    title: 'Bathroom Sanitization',
    description:
      'Our thorough bathroom cleaning eliminates 99.9% of germs. Tiles, grout, fixtures — everything gleams.',
    icon: 'ri-drop-line',
    image:
      'https://readdy.ai/api/search-image?query=professional%20cleaner%20scrubbing%20a%20modern%20white%20bathroom%20with%20chrome%20fixtures%2C%20sparkling%20clean%20tiles%20and%20glass%20shower%20door%2C%20bright%20lighting%20and%20cleaning%20supplies%20nearby%2C%20teal%20colored%20gloves%2C%20detailed%20professional%20cleaning%20service%20photography%2C%20warm%20natural%20tones&width=600&height=400&seq=vid-bath-002&orientation=landscape',
    duration: '1:30',
  },
  {
    title: 'Living Room Refresh',
    description:
      'Dusting, vacuuming, mopping, and polishing — we bring life back to your living spaces across Melbourne suburbs.',
    icon: 'ri-sofa-line',
    image:
      'https://readdy.ai/api/search-image?query=professional%20cleaner%20vacuuming%20a%20bright%20modern%20living%20room%20with%20large%20windows%20and%20natural%20light%2C%20minimalist%20furniture%2C%20clean%20hardwood%20floors%2C%20teal%20accent%20pillows%20on%20white%20sofa%2C%20warm%20inviting%20atmosphere%2C%20professional%20cleaning%20service%20photography&width=600&height=400&seq=vid-living-003&orientation=landscape',
    duration: '2:10',
  },
  {
    title: 'End of Lease Clean',
    description:
      'Moving out? Our Melbourne team ensures you get your full bond back with our comprehensive end-of-lease cleaning service.',
    icon: 'ri-key-line',
    image:
      'https://readdy.ai/api/search-image?query=empty%20modern%20apartment%20being%20cleaned%20by%20professional%20cleaner%2C%20bright%20empty%20rooms%20with%20clean%20white%20walls%20and%20polished%20timber%20floors%2C%20cleaning%20equipment%20and%20supplies%2C%20natural%20daylight%20through%20windows%2C%20move%20out%20cleaning%20service%2C%20warm%20tones%20professional%20photography&width=600&height=400&seq=vid-lease-004&orientation=landscape',
    duration: '3:15',
  },
];

export default function VideoShowcase() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  // Observe when the component scrolls into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  // Auto‑rotate the featured service every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % services.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Guard against an empty services array (defensive programming)
  if (!services.length) {
    return null;
  }

  return (
    <section
      ref={sectionRef}
      className="py-24 bg-white overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">
            See Us In Action
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-3 mb-4">
            Melbourne Cleaning Services
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base">
            Our dedicated team of 5 professionals delivers exceptional cleaning results across
            Melbourne and surrounding suburbs. 10 years of trusted service.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 items-start">
          {/* Main featured video card */}
          <div
            className={`lg:col-span-3 relative rounded-2xl overflow-hidden shadow-xl group cursor-pointer transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="w-full h-[420px] relative">
              {services.map((service, idx) => (
                <img
                  key={idx}
                  src={service.image}
                  alt={service.title}
                  className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-700 ${
                    idx === activeIndex ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              ))}

              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>

              {/* Play button */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-20 bg-teal-600/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  <i className="ri-play-fill text-white text-4xl ml-1"></i>
                </div>
              </div>

              {/* Duration badge */}
              <div className="absolute top-4 right-4 bg-black/60 text-white text-xs font-semibold px-3 py-1.5 rounded-md">
                {services[activeIndex].duration}
              </div>

              {/* Bottom info */}
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center">
                    <i
                      className={`${services[activeIndex].icon} text-white text-sm`}
                    ></i>
                  </div>
                  <span className="text-teal-300 text-sm font-semibold uppercase tracking-wide">
                    Featured Service
                  </span>
                </div>
                <h3 className="text-white text-2xl font-bold mb-1">
                  {services[activeIndex].title}
                </h3>
                <p className="text-gray-300 text-sm">
                  {services[activeIndex].description}
                </p>
              </div>
            </div>
          </div>

          {/* Side thumbnails */}
          <div className="lg:col-span-2 space-y-4">
            {services.map((service, idx) => (
              <button
                key={idx}
                onClick={() => setActiveIndex(idx)}
                className={`w-full flex items-center gap-4 p-4 rounded-xl transition-all duration-300 text-left cursor-pointer ${
                  idx === activeIndex
                    ? 'bg-teal-50 border-2 border-teal-600 shadow-md'
                    : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                } ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}
                style={{
                  transitionDelay: isVisible ? `${idx * 100 + 200}ms` : '0ms',
                }}
              >
                <div className="w-20 h-16 rounded-lg overflow-hidden flex-shrink-0 relative">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                    <i className="ri-play-fill text-white text-lg"></i>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-gray-900 text-sm mb-0.5 truncate">
                    {service.title}
                  </h4>
                  <p className="text-gray-500 text-xs truncate">
                    {service.description}
                  </p>
                  <span className="text-teal-600 text-xs font-semibold">
                    {service.duration}
                  </span>
                </div>
                {idx === activeIndex && (
                  <div className="w-2 h-2 bg-teal-600 rounded-full flex-shrink-0 animate-pulse"></div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Animated stats bar */}
        <div
          className={`mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 transition-all duration-700 delay-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {[
            {
              icon: 'ri-map-pin-line',
              label: 'Melbourne & Suburbs',
              value: '50+ Areas',
            },
            { icon: 'ri-team-line', label: 'Expert Cleaners', value: 'Team of 5' },
            { icon: 'ri-calendar-check-line', label: 'In Business', value: '10 Years' },
            {
              icon: 'ri-star-smile-line',
              label: 'Client Satisfaction',
              value: '99.5%',
            },
          ].map((stat, idx) => (
            <div
              key={idx}
              className="bg-gray-50 rounded-xl p-5 text-center hover:shadow-md transition-all"
            >
              <div className="w-10 h-10 bg-teal-600/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className={`${stat.icon} text-teal-600 text-xl`}></i>
              </div>
              <div className="text-xl font-bold text-gray-900">{stat.value}</div>
              <p className="text-gray-500 text-xs mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
